import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPlus } from '@fortawesome/pro-regular-svg-icons';
import cn from 'classnames';
import './NewCircleModalFinal.scss';

import SmartImage from '../../../General/SmartImage';
import ShopMySelect from '../../../General/ShopMySelect';
import Loader from '../../../Loader/Loader';

import { isLoggedIn, getCuratorGroups } from '../../../../Helpers/user_helpers';
import {
  getHierarchyOptions,
  generateHierarchyOptionValue,
  expandHierarchyOptionValue,
  getHierarchyOptionForDepartment
} from '../../../../Helpers/shop_helpers';

const NewCircleModalFinal = props => {
  const {
    user,
    shop,
    selectedCurators,
    selectedDepartments,
    circleTitle,
    setCircleTitle,
    circleHierarchy,
    setCircleHierarchy,
    onlyHasInitialCurators
  } = props;

  const activeHierarchyValue = generateHierarchyOptionValue(circleHierarchy.Industry_id, circleHierarchy.Department_id);
  const selectHierarchyValue = value => {
    const { Industry_id, Department_id } = expandHierarchyOptionValue(value);
    const newHierarchy = { Industry_id, Department_id };
    setCircleHierarchy(newHierarchy);
  };

  // Show circles in the final panel
  let curators = [];
  if (selectedCurators.length <= 1) {
    curators = [null, null, selectedCurators[0], null, null];
  } else if (selectedCurators.length <= 3) {
    curators = [null, selectedCurators[1], selectedCurators[0], selectedCurators[2], null];
  } else {
    curators = [selectedCurators[3], selectedCurators[1], selectedCurators[0], selectedCurators[2], selectedCurators[4]];
  }

  // UI
  const [isCreatingCircleForTab, setIsCreatingCircleForTab] = React.useState(null);
  const canCreateCircle = !!circleTitle.length;
  const cannotCreateCircleActionAlert = !circleTitle.length ? 'Please name your circle' : '';
  const headToLatestTab = () => headToTab('latest');
  const headToPopularTab = () => headToTab('popular');
  const headToTab = async tab => {
    const createAndHeadToTab = async () => {
      setIsCreatingCircleForTab(tab);
      await props.createCircleAndHeadToTab(tab);
      setIsCreatingCircleForTab(null);
    };

    if (!isLoggedIn(props.user)) {
      props.openAuthModal('register-shopper');
      return;
    } else {
      createAndHeadToTab();
    }
  };

  // If they only have one department selected, default that for the hierarchy
  React.useEffect(() => {
    if (selectedDepartments.length === 1) {
      setCircleHierarchy({
        Industry_id: selectedDepartments[0].Industry_id,
        Department_id: selectedDepartments[0].id
      });
    }
  }, [selectedDepartments.length]);

  // Based on curators
  const hasCuratorGroups = getCuratorGroups(user).length > 0;

  return (
    <div className='new-circle-modal-final-container'>
      <div className='final-content'>
        <div className='back-container' onClick={props.backFromFinalPanel}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <div className='back-btn-text'>Back</div>
        </div>
        <div className='curator-photos'>
          {curators.map((curator, idx) => {
            if (!curator) return <div key={`empty-curator-${idx}`} />;
            return (
              <div
                className={cn('photo', {
                  first: idx === 0,
                  second: idx === 1,
                  third: idx === 2,
                  fourth: idx === 3,
                  fifth: idx === 4
                })}
                key={curator.id}
              >
                <SmartImage src={curator.image} alt={curator.name} fallbackText={curator.name} />
              </div>
            );
          })}
        </div>
        <div className='circle-title-input-container'>
          <input
            className='circle-title-input'
            autoFocus
            value={circleTitle}
            onChange={e => setCircleTitle(e.target.value)}
            placeholder={hasCuratorGroups ? 'Name Your Circle' : 'My First Circle'}
          />
        </div>
        <div className='trusted-for-select-container'>
          <ShopMySelect
            noStyling
            options={getHierarchyOptions(shop)}
            value={activeHierarchyValue}
            placeholder='Expertise Focus'
            onChangeValue={selectHierarchyValue}
          />
        </div>
        {onlyHasInitialCurators && (
          <div className='initial-curators-warning'>
            <div>
              Circles are better with more than one curator, click{' '}
              <span
                className='add-more-link'
                onClick={() => {
                  window.__ADD_EVENT__('Shop - Create Circle - Add More Curators Affordance');
                  props.goBackToFirstPanel();
                }}
              >
                here
              </span>{' '}
              to add more.
            </div>
          </div>
        )}
        <div className='action-btns'>
          {onlyHasInitialCurators ? (
            <>
              <div
                onClick={() => {
                  window.__ADD_EVENT__('Shop - Create Circle - Add More Curators Affordance');
                  props.goBackToFirstPanel();
                }}
                className={cn('action-btn secondary', { disabled: !canCreateCircle })}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add More Curators
              </div>
              <div
                onClick={headToLatestTab}
                className={cn('action-btn primary', { disabled: !canCreateCircle, loading: isCreatingCircleForTab === 'latest' })}
              >
                {cannotCreateCircleActionAlert ? cannotCreateCircleActionAlert : 'Shop Latest Finds'}
                {isCreatingCircleForTab === 'latest' && <Loader size={64} />}
              </div>
            </>
          ) : (
            <>
              <div
                onClick={headToPopularTab}
                className={cn('action-btn secondary', { disabled: !canCreateCircle, loading: isCreatingCircleForTab === 'popular' })}
              >
                Create & Shop Most Popular
                {isCreatingCircleForTab === 'popular' && <Loader size={64} />}
              </div>
              <div
                onClick={headToLatestTab}
                className={cn('action-btn primary', { disabled: !canCreateCircle, loading: isCreatingCircleForTab === 'latest' })}
              >
                Create & Shop Latest Finds
                {isCreatingCircleForTab === 'latest' && <Loader size={64} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

NewCircleModalFinal.propTypes = {
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  circleTitle: PropTypes.string.isRequired,
  setCircleTitle: PropTypes.func.isRequired,
  circleHierarchy: PropTypes.object.isRequired,
  setCircleHierarchy: PropTypes.func.isRequired,
  goToPreviousPanel: PropTypes.func.isRequired,
  createCircleAndHeadToTab: PropTypes.func.isRequired,
  backFromFinalPanel: PropTypes.func.isRequired,
  openAuthModal: PropTypes.func.isRequired,
  onlyHasInitialCurators: PropTypes.bool.isRequired
};

export default NewCircleModalFinal;
